import { AppTheme } from '../types/index.ts';

const PULSE_SEC = 2.4;
const MENU_SEC = 0.18;

const EASE_OUT = [0.23, 1, 0.32, 1] as const;

// Значения шкалы — пиксели; мелкие шаги сохраняют размеры существующих контролов.
const SPACE = {
  1: '1px',
  2: '2px',
  3: '3px',
  4: '4px',
  5: '5px',
  6: '6px',
  7: '7px',
  8: '8px',
  10: '10px',
  11: '11px',
  12: '12px',
  14: '14px',
  15: '15px',
  16: '16px',
  18: '18px',
  20: '20px',
  24: '24px',
  28: '28px',
  32: '32px',
  36: '36px',
  40: '40px',
  44: '44px',
  48: '48px',
  52: '52px',
  56: '56px',
  64: '64px',
  72: '72px',
  80: '80px',
  96: '96px',
} as const;

export const BASE_TOKENS = {
  space: {
    ...SPACE,
    // Границы берём из шкалы, средняя величина задаёт рост отступа относительно viewport.
    fluid: (min: keyof typeof SPACE, viewport: number, max: keyof typeof SPACE) =>
      `clamp(${SPACE[min]}, ${viewport}vw, ${SPACE[max]})`,
    // Боковое поле не меньше выреза экрана (iPhone в альбомной ориентации, viewport-fit=cover).
    // max, а не сумма: колонки шапки, каталога и контента остаются на одной линии
    safe: (value: string) =>
      `max(${value}, env(safe-area-inset-left, 0px), env(safe-area-inset-right, 0px))`,
    // Линия контента страниц и подвала
    page: `max(clamp(${SPACE[24]}, 5vw, ${SPACE[80]}), env(safe-area-inset-left, 0px), env(safe-area-inset-right, 0px))`,
    // То же поле без выреза — только для атрибута sizes: env() там недопустим, и браузер
    // отбрасывает всё значение, считая фото шириной в экран. Вырез в sizes — погрешность в десятки px
    pageBase: `clamp(${SPACE[24]}, 5vw, ${SPACE[80]})`,
    catalogRow: `clamp(${SPACE[16]}, 1.6vw, ${SPACE[24]})`,
    projectGap: `clamp(${SPACE[24]}, 4vw, ${SPACE[64]})`,
    sectionGap: `clamp(${SPACE[48]}, 6vw, ${SPACE[80]})`,
    sectionInset: `clamp(${SPACE[32]}, 4vw, ${SPACE[56]})`,
    pageTop: `clamp(${SPACE[36]}, 5vw, ${SPACE[72]})`,
    projectBottom: `clamp(${SPACE[40]}, 5vw, ${SPACE[64]})`,
  },
  fonts: {
    // Метрические fallback из font-fallbacks.css; обычные семейства — если local() недоступен.
    heading:
      '"Oswald Variable", "Oswald", "Oswald Arial Narrow Fallback", "Oswald Arial Fallback", "Arial Narrow", Arial, sans-serif',
    body: '"Roboto Condensed Variable", "Roboto Condensed", "Roboto Condensed Arial Narrow Fallback", "Roboto Condensed Arial Fallback", "Arial Narrow", Arial, sans-serif',
  },
  type: {
    hero: 'clamp(2.5rem, 1.75rem + 3vw, 4.5rem)',
    page: 'clamp(2rem, 1.5rem + 2.2vw, 3.5rem)',
    item: 'clamp(1.75rem, 1.5rem + 0.8vw, 2.25rem)',
    section: 'clamp(1.5rem, 1.25rem + 1.1vw, 2.25rem)',
    project: 'clamp(1.25rem, 1rem + 1.1vw, 2rem)',
    catalog: 'clamp(1rem, 0.9rem + 0.35vw, 1.25rem)',
    menu: 'clamp(1.125rem, 1rem + 0.35vw, 1.375rem)',
    navigation: '1rem',
    body: 'clamp(1rem, 0.95rem + 0.2vw, 1.125rem)',
    caption: '0.875rem',
    errorCode: '4rem',
    errorTitle: '1.5rem',
    brandTagline: '0.5rem',
    legalHeading: 'clamp(1.125rem, 1.6vw, 1.375rem)',
    dialogTitle: 'clamp(1.125rem, 2vw, 1.5rem)',
  },
  radius: { small: '8px', line: '1px', medium: '12px', control: '8px' },
  layout: {
    contentMax: '1600px',
    heroImageSize: 'clamp(420px, 52vw, 740px)',
    heroContentSize: 'min(660px, 52vw)',
    controlHeight: '44px',
    actionHeight: '48px',
    legalMax: '860px',
    dialogMax: '760px',
    dialogMaxHeight: '900px',
    catalogThumb: 'clamp(80px, 8vw, 112px)',
    menuThumb: 'clamp(64px, 6vw, 96px)',
    catalogNumber: 'clamp(32px, 3vw, 44px)',
    catalogArrow: 'clamp(28px, 3vw, 40px)',
  },
  transitions: {
    default: '0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    fast: '0.15s ease',
    slow: '0.5s ease',
  },
  // Движение: ease-out для появления и реакции, длительности короткие — сайт для работы, не для шоу
  motion: {
    easeOut: `cubic-bezier(${EASE_OUT.join(', ')})`,
    // То же для Motion (JS): он принимает кривую массивом и длительность в секундах, а не CSS-строки
    easeOutCurve: EASE_OUT,
    menuSec: MENU_SEC,
    menuDuration: `${MENU_SEC}s`,
    fast: '150ms',
    reducedDuration: '0.01ms',
    // Жест логотипа: мягкая кривая с длинным «дотягиванием» — на коротких длительностях выглядит резко
    hover: '600ms',
    easeOutSoft: 'cubic-bezier(0.2, 1, 0.3, 1)',
    // Появление героя: easeOut выше слишком крутой (¾ пути за первые ~90 мс — текст «выстреливает»),
    // easeOutCubic замедляется равномернее, движение успевает прочитаться
    easeEnter: 'cubic-bezier(0.33, 1, 0.68, 1)',
    enter: '800ms',
    // Те же кривая и подъём для Motion: появление секций при прокрутке — короче героя, не тормозит чтение
    easeEnterCurve: [0.33, 1, 0.68, 1] as const,
    revealSec: 0.6,
    revealShiftPx: 20,
    // Досчёт цифр статистики: дольше появления секции, чтобы рост читался, но не держал взгляд
    countSec: 1.4,
    // Лента логотипов: секунд на один знак — скорость постоянна при любой длине списка
    marqueeItemSec: 6,
    // Точки карты поставок расходятся от производства: за это время доходят до самой дальней
    mapSpreadSec: 1.2,
    // Пульс у точек с идущими работами — медленный, чтобы не отвлекать от карты
    pulseSec: PULSE_SEC,
    pulseDuration: `${PULSE_SEC}s`,
    // Шаг каскада заголовок → описание → кнопки: кнопки появляются через ~240 мс, CTA не ждёт
    stagger: '120ms',
    // Полоса загрузки страницы: быстрый переход её не показывает, дольше — видно, что клик принят
    progressDelayMs: 400,
    progressSweep: '1.2s',
  },
  // Тени слоёв поверх контента и выделения выбранного элемента.
  shadows: {
    selection: '0 0 0 1px var(--color-primary)',
    overlay: '0 2px 6px rgba(0, 0, 0, 0.12), 0 12px 24px rgba(0, 0, 0, 0.16)',
  },
  // Высота шапки вместе с верхним вырезом экрана (viewport-fit=cover): панели и липкие полосы
  // встают под шапку по этому значению. Строка самой шапки — headerBarHeight
  headerHeight: 'calc(64px + env(safe-area-inset-top, 0px))',
  headerBarHeight: '64px',
  // Липкие полосы страниц — под шапкой (1001) и выпадающими меню (1000); полоса загрузки — над всем
  layers: {
    header: 1001,
    menu: 1000,
    stickyBar: 900,
    consent: 1100,
    dialogBackdrop: 1100,
    dialog: 1101,
    progress: 1200,
  },
  defaultPrimary: '#8fb3dc',
} as const;

// Палитра «сланец + сталь»: холодные графитовые поверхности и один стальной синий акцент.
// Акцент — только для действий (кнопки, активные пункты, фокус), заголовки и цифры — цветом текста.
// Синий дополняет оранжевые детали оборудования на фото — продукт выделяется на фоне.
// Контраст пар проверен по WCAG 2.2 AA (обычный текст ≥ 4.5:1) в обеих темах
const themeVariants = {
  [AppTheme.Dark]: {
    primary: '#8fb3dc', // 8.35:1 на bg, 7.44:1 на card
    bg: '#11161c',
    card: '#1a212b',
    cardHover: '#222a36',
    border: '#2c3542',
    controlBorder: '#778496', // рамки полей: не менее 3:1 на bg, card и cardHover
    text: '#e8edf3',
    textMuted: '#9aa6b4', // 6.55:1 на card
    ctaTextColor: '#0f1a26', // 8.07:1 на primary
    focus: '#8fb3dc',
    danger: '#ff9b9b', // ошибки форм, 8.1:1 на card
  },
  [AppTheme.Light]: {
    primary: '#2c5a8c', // 6.41:1 на bg, белый текст на нём 7.12:1
    bg: '#f1f3f6',
    card: '#ffffff',
    cardHover: '#f7f9fb',
    border: '#dde2e8',
    controlBorder: '#778391',
    text: '#121820',
    textMuted: '#5b6573', // 5.32:1 на bg
    ctaTextColor: '#ffffff',
    focus: '#2c5a8c',
    danger: '#b42318', // ошибки форм, 6.5:1 на card
  },
} as const;

// Фон под фотографиями товаров: снимки сделаны на белом, в обеих темах подложка белая
const MEDIA_SURFACE = '#ffffff';

type ColorToken = keyof (typeof themeVariants)[AppTheme.Dark];

const COLOR_TOKENS = Object.keys(themeVariants[AppTheme.Dark]) as ColorToken[]; // as: Object.keys теряет тип ключей

// camelCase → kebab-case: cardHover → --color-card-hover
const cssVarName = (token: ColorToken) =>
  `--color-${token.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;

/** Значения цветов темы как CSS-переменные — для `:root[data-theme]` в CSS */
export const themeCssVars = (type: AppTheme): string =>
  COLOR_TOKENS.map((token) => `${cssVarName(token)}: ${themeVariants[type][token]};`).join(' ');

/** Фон страницы темы — для `<meta name="theme-color">` (браузерная панель не понимает var()) */
export const themeBackground = (type: AppTheme): string => themeVariants[type].bg;

/** Общие CSS-переменные токенов; палитры переключаются отдельно. */
export const baseCssVars = (): string => {
  const declarations = (prefix: string, values: Record<string, unknown>) =>
    Object.entries(values).flatMap(([name, value]) =>
      typeof value === 'string'
        ? [
            `--${prefix}-${name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)}: ${value};`,
          ]
        : [],
    );
  return [
    ...declarations('space', BASE_TOKENS.space),
    ...declarations('font', BASE_TOKENS.fonts),
    ...declarations('type', BASE_TOKENS.type),
    ...declarations('motion', BASE_TOKENS.motion),
    ...declarations('radius', BASE_TOKENS.radius),
    ...declarations('layout', BASE_TOKENS.layout),
    `--layout-header-height: ${BASE_TOKENS.headerHeight};`,
    ...declarations('transition', BASE_TOKENS.transitions),
    ...declarations(
      'layer',
      Object.fromEntries(
        Object.entries(BASE_TOKENS.layers).map(([key, value]) => [key, String(value)]),
      ),
    ),
    ...declarations('shadow', BASE_TOKENS.shadows),
    `--color-media-surface: ${MEDIA_SURFACE};`,
  ].join(' ');
};

/** Полупрозрачный вариант цвета: склейка hex с альфой (`${color}80`) с CSS-переменными не работает */
export const withAlpha = (color: string, percent: number): string =>
  `color-mix(in srgb, ${color} ${percent}%, transparent)`;
